import React, { useCallback, useMemo, useState } from 'react';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useDebounce from 'hooks/useDebounce';

import VendorRequestItem from '../../VendorRequestItem';
import { getGroupDuration } from '../../vendorRequests.groups';
import { VendorRequestsTestIds } from '../../vendorRequests.testIds';
import VendorRequestStatusLabel from '../../VendorRequestStatusLabel';
import { isTerminalVendorRequestStatus } from '../../VendorRequestStatusLabel/VendorRequestStatusLabel.constants';
import { useGroupItems } from '../hooks/useGroupItems';
import {
  EMPTY_ITEMS_MESSAGE,
  ERROR_ITEMS_MESSAGE,
  FILTERED_EMPTY_ITEMS_MESSAGE,
  GroupItemsStatus,
  ITEMS_SEARCH_DEBOUNCE_MS,
  LOAD_MORE_LABEL,
} from '../VendorRequestDetailDrawer.constants';
import {
  contentWrapperStyles,
  groupHeaderStyles,
  itemsListStyles,
  statusBlockStyles,
} from '../VendorRequestDetailDrawer.styles';
import { formatItemCount, formatMatchingItemCount } from '../VendorRequestDetailDrawer.utils';
import VendorRequestItemsFilters from '../VendorRequestItemsFilters';

const VendorRequestDrawerBody = ({ group, applicationId }) => {
  const [operationType, setOperationType] = useState('');
  const [operationTypeFilter, setOperationTypeFilter] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [vendorRequestId, setVendorRequestId] = useState('');
  const [vendorRequestIdFilter, setVendorRequestIdFilter] = useState('');

  const setOperationTypeFilterDebounced = useDebounce(
    setOperationTypeFilter,
    ITEMS_SEARCH_DEBOUNCE_MS,
  );
  const setVendorRequestIdFilterDebounced = useDebounce(
    setVendorRequestIdFilter,
    ITEMS_SEARCH_DEBOUNCE_MS,
  );

  const itemFilters = useMemo(
    () => ({
      operationType: operationTypeFilter,
      statusIds: statuses.map(s => s.id),
      vendorRequestId: vendorRequestIdFilter,
    }),
    [operationTypeFilter, statuses, vendorRequestIdFilter],
  );

  const {
    items,
    totalCount: matchingCount,
    status: itemsStatus,
    hasMore,
    loadMore,
  } = useGroupItems({
    group,
    applicationId,
    itemFilters,
  });

  const status = _.get(group, 'status');
  const itemCount = _.get(group, 'itemCount', items.length);
  const correlationId = _.get(group, 'correlationId');
  const groupDuration = getGroupDuration(group);
  const canSearchItems = !_.isNil(correlationId);
  const hasActiveItemFilters =
    operationTypeFilter.trim().length > 0 ||
    statuses.length > 0 ||
    vendorRequestIdFilter.trim().length > 0;

  const isInitialLoading = itemsStatus === GroupItemsStatus.LOADING;
  const isLoadingMore = itemsStatus === GroupItemsStatus.LOADING_MORE;
  const isLoadError = itemsStatus === GroupItemsStatus.ERROR;
  const isEmpty = itemsStatus === GroupItemsStatus.LOADED && items.length === 0;
  const isMatchCountKnown =
    hasActiveItemFilters && (itemsStatus === GroupItemsStatus.LOADED || isLoadingMore);
  const itemCountLabel = isMatchCountKnown
    ? formatMatchingItemCount(matchingCount, itemCount)
    : formatItemCount(itemCount);

  // A single-item group has no Requested/terminal pair to fold, so open it; otherwise mirror the
  // table's "reads as its terminal state" — terminal items expanded, the earlier Requested collapsed.
  const isItemExpanded = item =>
    itemCount === 1 || isTerminalVendorRequestStatus(_.get(item, 'status'));

  const onOperationTypeChange = useCallback(
    event => {
      const { value } = event.target;

      setOperationType(value);
      setOperationTypeFilterDebounced(value);
    },
    [setOperationTypeFilterDebounced],
  );

  const onStatusesChange = useCallback((_event, value) => setStatuses(value), []);

  const onVendorRequestIdChange = useCallback(
    event => {
      const { value } = event.target;

      setVendorRequestId(value);
      setVendorRequestIdFilterDebounced(value);
    },
    [setVendorRequestIdFilterDebounced],
  );

  return (
    <Box sx={contentWrapperStyles}>
      <Box sx={groupHeaderStyles}>
        {status && <VendorRequestStatusLabel status={status} />}
        <Typography data-testid={VendorRequestsTestIds.GROUP_ITEM_COUNT} variant="medium14">
          {itemCountLabel}
        </Typography>
        {groupDuration && (
          <Typography
            color="text.secondary"
            data-testid={VendorRequestsTestIds.GROUP_DURATION}
            variant="regular12"
          >
            Duration {groupDuration}
          </Typography>
        )}
        {correlationId && (
          <Typography color="text.secondary" variant="regular12">
            Correlation {correlationId}
          </Typography>
        )}
      </Box>
      {canSearchItems && (
        <VendorRequestItemsFilters
          operationType={operationType}
          statuses={statuses}
          vendorRequestId={vendorRequestId}
          onOperationTypeChange={onOperationTypeChange}
          onStatusesChange={onStatusesChange}
          onVendorRequestIdChange={onVendorRequestIdChange}
        />
      )}
      <Box data-testid={VendorRequestsTestIds.ITEMS_LIST} sx={itemsListStyles}>
        {isInitialLoading && (
          <Box data-testid={VendorRequestsTestIds.ITEMS_LOADING} sx={statusBlockStyles}>
            <CircularProgress size={20} />
          </Box>
        )}
        {isLoadError && (
          <Typography
            color="error"
            data-testid={VendorRequestsTestIds.ITEMS_ERROR}
            variant="regular14"
          >
            {ERROR_ITEMS_MESSAGE}
          </Typography>
        )}
        {isEmpty && (
          <Typography
            color="text.secondary"
            data-testid={VendorRequestsTestIds.ITEMS_EMPTY}
            variant="regular14"
          >
            {hasActiveItemFilters ? FILTERED_EMPTY_ITEMS_MESSAGE : EMPTY_ITEMS_MESSAGE}
          </Typography>
        )}
        {items.map(item => (
          <VendorRequestItem
            defaultExpanded={isItemExpanded(item)}
            item={item}
            key={_.get(item, 'id')}
          />
        ))}
        {hasMore && (
          <Button
            data-testid={VendorRequestsTestIds.LOAD_MORE_BUTTON}
            disabled={isLoadingMore}
            size="small"
            variant="outlined"
            onClick={loadMore}
          >
            {LOAD_MORE_LABEL}
          </Button>
        )}
      </Box>
    </Box>
  );
};

VendorRequestDrawerBody.propTypes = {
  applicationId: PropTypes.string.isRequired,
  group: PropTypes.shape({
    groupKey: PropTypes.string,
    status: PropTypes.string,
    correlationId: PropTypes.string,
    itemCount: PropTypes.number,
  }).isRequired,
};

export default VendorRequestDrawerBody;
